/**
 * BLOCKS-section export builder (extracted from `dxfBridge.export.ts`).
 *
 * Owns the `DrawingDocument.blocks` → `DxfBlocksSection` projection: the
 * regular user-block path, the #314 layout/paper-space relocation path, the
 * provenance-aware ATTDEF de-dup, and the raw-entity-group passthrough. Split
 * out so `dxfBridge.export.ts` stays under the 2000-line cap (file-size rule).
 * `dxfBridge.export.ts` re-exports `rawEntityGroupToDxfUnsupported` +
 * `buildBlocksSectionFromDrawingBlocks` so the public barrel surface is
 * unchanged for external importers.
 */

import {
  advanceControlDepth,
  type DxfBlockDefinition,
  type DxfBlocksSection,
  type DxfEntity,
  type DxfGroup,
  type DxfUnsupportedEntity,
} from "@dxf-ai-editor/dxf-io";

import {
  drawingAttdefToDxfAttdef,
  toDxfEntity,
} from "./dxfBridge.entityConversion";
import { isLayoutBlockName, isPaperSpaceBlockName } from "./blockNaming";
import {
  blockEmitsOwnTypedChildren,
  buildPaperSpaceLayoutPlan,
  findPairedPaperSpaceEntry,
  type PaperSpaceLayoutPlan,
} from "./dxfBridge.paperSpaceLayout";
import {
  type AttdefEntity,
  type BlockDefinition,
  type DrawingDocument,
  type DrawingEntity,
  type DrawingSpace,
  type RawEntityGroup,
} from "./model";

/**
 * Inject a `8/<layer>` group into a raw unsupported entity's group list when the
 * source carried NONE (FINDING 2, T9 round-2 edge — the conformant from-scratch
 * export forces code 8 on EVERY entity, but the verbatim raw-group passthrough
 * never had it injected → a raw unsupported entity silently omitted code 8,
 * violating the "code 8 on every entity" invariant the typed path enforces via
 * `forceLayerEmit`). The injection is conformant-path-scoped (the caller only
 * passes `forceLayerEmit` on the reminted/conformant export); the imported
 * round-trip path leaves the raw bytes byte-identical.
 *
 * Placement mirrors the conventional DXF entity layout
 * (`0/<TYPE> 5/<h> 330/<owner> 100/AcDbEntity 8/<layer>`): the `8` is inserted
 * immediately AFTER the `100/AcDbEntity` subclass marker when present (the
 * canonical AcDbEntity slot), else after the first DEPTH-0 owner `330`, else
 * after the first own-handle `5`, else right after the `0/<TYPE>` header — every
 * position ezdxf/AutoCAD accept for the common AcDbEntity layer group. Default
 * layer "0" (the raw entity carried no explicit layer, so it inherits the default).
 *
 * FINDING 2 (T9 round-2) — the owner-`330` / own-handle-`5` search MUST skip any
 * marker nested INSIDE a `102 {…102 }` control block (a leading
 * `102 {ACAD_REACTORS … 330 … 102 }` reactor block carries a NESTED `330` that
 * is a reactor cross-link, NOT the structural entity owner — this is the EXACT
 * pattern #384 round-17 BUG-2 solved). Splicing `8/0` after that nested `330`
 * would corrupt the 102 block AND mis-place the layer. We reuse the shared
 * `advanceControlDepth` (dxf-io controlBlockDepth — the single source of truth
 * also used by parseDynamicBlockObject / assocArrayParser / spatialFilterParser)
 * to track 102 nesting and only treat a DEPTH-0 `330`/`5` as the insertion owner.
 * The `100/AcDbEntity` subclass marker is itself depth-0 (it never appears inside
 * a 102 reactor block) so its position lookup needs no depth guard.
 */
function injectRawEntityLayerGroup(groups: DxfGroup[]): DxfGroup[] {
  // Already carries an explicit layer → leave verbatim (no double-emit).
  if (groups.some((g) => g.code === 8)) return groups;
  const layerGroup: DxfGroup = { code: 8, value: "0" };
  // First preference: immediately after the canonical `100/AcDbEntity` subclass
  // marker (depth-0 by construction — never nested in a 102 reactor block).
  let at = -1;
  const acdbIdx = groups.findIndex(
    (g) => g.code === 100 && g.value === "AcDbEntity",
  );
  if (acdbIdx !== -1) {
    at = acdbIdx + 1;
  } else {
    // No subclass marker — find the first DEPTH-0 owner `330`, else the first
    // DEPTH-0 own-handle `5`, skipping any marker nested inside a balanced
    // `102 {…102 }` control block (reactor cross-links are NOT the owner).
    let controlDepth = 0;
    let owner330At = -1;
    let handle5At = -1;
    for (let i = 0; i < groups.length; i += 1) {
      const g = groups[i];
      const nextDepth = advanceControlDepth(g, controlDepth);
      if (nextDepth !== undefined) {
        controlDepth = nextDepth;
        continue;
      }
      if (controlDepth !== 0) continue; // nested in a 102 block — skip
      if (owner330At === -1 && g.code === 330) owner330At = i + 1;
      if (handle5At === -1 && g.code === 5) handle5At = i + 1;
    }
    at = owner330At !== -1 ? owner330At : handle5At;
  }
  // Fall back to right after the `0/<TYPE>` header (index 0) — never before it.
  if (at === -1) at = 1;
  return [...groups.slice(0, at), layerGroup, ...groups.slice(at)];
}

/**
 * Convert a RawEntityGroup back into a DxfUnsupportedEntity record for the
 * writer. The writer's `serializeEntity` branch returns the `groups[]` as-is
 * when `kind === "unsupported-entity"`, which is how we preserve verbatim
 * bytes for ACAD_TABLE / SPLINE / XRECORD and similar unsupported types.
 * The first group is the `{code: 0, value: entityType}` header the parser
 * stripped when it split the schema into `entityType` + `groups`.
 *
 * FINDING 2 (T9 round-2) — when `forceLayerEmit` is true (the conformant /
 * reminted from-scratch export) and the raw entity carried NO explicit layer,
 * inject a `8/0` group so it honours the "code 8 on every entity" invariant the
 * typed path forces via `forceLayerEmit`. Defaults to `false` so the carry-
 * forward / imported round-trip stays byte-identical (no injection).
 */
export function rawEntityGroupToDxfUnsupported(
  raw: RawEntityGroup,
  forceLayerEmit: boolean = false,
): DxfUnsupportedEntity {
  let groups: DxfGroup[] = [
    { code: 0, value: raw.entityType },
    ...raw.groups.map((g) => ({ code: g.code, value: g.value })),
  ];
  if (forceLayerEmit) groups = injectRawEntityLayerGroup(groups);
  return {
    kind: "unsupported-entity",
    type: raw.entityType,
    groups,
  };
}

/**
 * A frozen empty ATTDEF-identity set — passed by the EXTRA-tab paper-block
 * branch, whose paired SPACE's `DrawingEntity[]` can never carry a typed ATTDEF
 * (the union has no Attdef variant), so there is nothing to de-dup the raw
 * ATTDEFs against. Hoisted so the per-block loop does not allocate a fresh set.
 */
const EMPTY_ATTDEF_IDENTITY_SET: ReadonlySet<string> = new Set<string>();

/**
 * Append a block's `rawEntityGroups` to a child-entity list, applying the
 * provenance-aware ATTDEF de-dup the BLOCKS builder uses in BOTH the
 * regular-block branch and the #314 EXTRA-tab paper-block branch (F2). Shared so
 * the two sites have a SINGLE source of truth for the de-dup rule (no jscpd
 * clone).
 *
 * FROM-SCRATCH (`docIsImported === false`) — a RAW ATTDEF whose identity (tag,
 * group 2, OR handle, group 5) was already emitted as a TYPED ATTDEF is
 * suppressed (the typed one carries the reminted, ref-targeted handle). IMPORTED
 * — every raw ATTDEF emits verbatim (the matching typed projection was already
 * suppressed by the caller), preserving carry-forward byte-fidelity. A raw ATTDEF
 * with no typed counterpart emits on either path. Non-ATTDEF raw groups always
 * emit verbatim.
 */
function appendRawEntityGroupsWithAttdefDedup(
  childDxfEntities: DxfEntity[],
  rawEntityGroups: RawEntityGroup[] | undefined,
  docIsImported: boolean,
  emittedTypedAttdefTags: ReadonlySet<string>,
  emittedTypedAttdefHandles: ReadonlySet<string>,
  /**
   * FINDING 1 (T9 round-2) — thread the conformant `forceLayerEmit` flag so a
   * RAW unsupported child of a BLOCK (a copied/imported block payload with no
   * group 8) gets `8/0` injected on the conformant / from-scratch path, honouring
   * the "code 8 on every entity" invariant. The `export.ts` document-level raw
   * passthrough already threaded `reminted` (round-3); this is the BLOCKS-section
   * sibling site. Defaults to `false` so the carry-forward / imported round-trip
   * stays byte-identical (no injection).
   */
  forceLayerEmit: boolean = false,
): void {
  for (const raw of rawEntityGroups ?? []) {
    if (!docIsImported && raw.entityType === "ATTDEF") {
      let rawTag = "";
      let rawHandle = "";
      for (const g of raw.groups) {
        if (g.code === 2 && g.value.length > 0) {
          rawTag = g.value.trim().toUpperCase();
        } else if (g.code === 5 && g.value.length > 0) {
          rawHandle = g.value.trim().toLowerCase();
        }
      }
      const coveredByTyped =
        (rawTag.length > 0 && emittedTypedAttdefTags.has(rawTag)) ||
        (rawHandle.length > 0 && emittedTypedAttdefHandles.has(rawHandle));
      if (coveredByTyped) continue; // typed ATTDEF already emitted this identity
    }
    childDxfEntities.push(rawEntityGroupToDxfUnsupported(raw, forceLayerEmit));
  }
}

/**
 * Build a block's OWN children (typed `block.entities` + raw `block.rawEntityGroups`
 * with provenance-aware ATTDEF de-dup) for a block whose own def is the
 * edit-authoritative copy of its content: a REGULAR user block AND an UNPAIRED
 * suffixed `*Paper_Space<N>` paper block (F3', round-2). Shared so the two sites
 * have a SINGLE source of truth for the per-block ATTDEF de-dup rule (no jscpd
 * clone). PAIRED layout blocks (`*Model_Space`, the first `*Paper_Space`, a PAIRED
 * extra tab) do NOT use this — their supported content is canonical in ENTITIES /
 * the paired space, handled by the dedicated branches.
 *
 * FIX D (round-9 SG-1) + BUG 3 (round-10/12, PER-ATTDEF de-dup) + BUG 2 (round-13):
 * an IMPORTED block captures each ATTDEF BOTH as a typed `Attdef` child AND as a
 * raw `block.rawEntityGroups` ATTDEF (import.ts), so emitting BOTH copies would
 * DOUBLE-emit. Exactly one survives per identity (tag, group 2, AND/OR handle,
 * group 5). WHICH one depends on provenance:
 *
 *   FROM-SCRATCH / reminted doc — `ensureConformantSkeleton` RE-MINTS the typed
 *     ATTDEF's handle and rewrites every MLEADER/1005 ref to that NEW typed
 *     handle. The surviving ATTDEF MUST be the TYPED one (the ref target); the
 *     matching RAW one (a different, un-rewritten handle) is suppressed, else the
 *     rewritten ref dangles (round-12 P2).
 *
 *   IMPORTED-SOURCE doc — `ensureConformantSkeleton` EARLY-RETURNS, so the typed
 *     ATTDEF is NEVER reminted and no ref is rewritten; the refs still point at
 *     the raw ATTDEF's ORIGINAL handle. The raw copy is the carry-forward
 *     byte-faithful round-trip, so the surviving ATTDEF MUST be the RAW one; the
 *     matching TYPED projection is suppressed.
 *
 * An ATTDEF carried by only ONE channel always emits, on either path. The
 * imported-source discriminator (`docIsImported`) MUST be the SAME predicate
 * `ensureConformantSkeleton` early-returns on (`isImportedSourceDocument` =
 * provenance marker OR imported raw SECTION source) — threaded from the orchestrator.
 */
function buildBlockOwnChildren(
  name: string,
  block: BlockDefinition,
  docIsImported: boolean,
  forceLayerEmit: boolean,
  /**
   * round-4 FINDING 1 — the document's full blocks map, threaded down to
   * `toDxfEntity` -> `drawingAttribToDxfAttrib` so a NESTED "Block" child
   * (an INSERT baked into another block's own definition, carrying its own
   * ATTRIB rows) can resolve an unset `overridePosition` against its
   * referenced ATTDEF template, same as the top-level ENTITIES path
   * (dxfBridge.export.ts).
   */
  blocks: Record<string, BlockDefinition>,
): DxfEntity[] {
  // Synthetic DrawingSpace so we can reuse `toDxfEntity`'s common-props plumbing
  // (layer / handle / xdata). It only needs the `id` / `kind` / `name` fields the
  // existing machinery inspects, plus the schema-required arrays.
  const blockSpace: DrawingSpace = {
    id: `__block:${name}`,
    kind: "model",
    name,
    entities: [],
    refeditWorkingSet: [],
  };
  const rawAttdefTags = new Set<string>();
  const rawAttdefHandles = new Set<string>();
  for (const raw of block.rawEntityGroups ?? []) {
    if (raw.entityType !== "ATTDEF") continue;
    for (const g of raw.groups) {
      if (g.code === 2 && g.value.length > 0) {
        rawAttdefTags.add(g.value.trim().toUpperCase());
      } else if (g.code === 5 && g.value.length > 0) {
        rawAttdefHandles.add(g.value.trim().toLowerCase());
      }
    }
  }
  const emittedTypedAttdefTags = new Set<string>();
  const emittedTypedAttdefHandles = new Set<string>();
  const childDxfEntities: DxfEntity[] = [];
  for (const child of block.entities) {
    if (child.type === "Attdef") {
      const attdef = child as AttdefEntity;
      const tagKey = attdef.tag.trim().toUpperCase();
      const handleKey = (attdef.handle ?? "").trim().toLowerCase();
      // IMPORTED — suppress the typed projection when a byte-faithful RAW
      // counterpart covers its identity; the raw copy emits below, verbatim.
      if (docIsImported) {
        const coveredByRaw =
          (tagKey.length > 0 && rawAttdefTags.has(tagKey)) ||
          (handleKey.length > 0 && rawAttdefHandles.has(handleKey));
        if (coveredByRaw) continue; // raw ATTDEF emits this identity verbatim
      } else {
        // FROM-SCRATCH — the typed (reminted) ATTDEF is the ref target. Record
        // its identity so the matching raw counterpart is suppressed below.
        if (tagKey.length > 0) emittedTypedAttdefTags.add(tagKey);
        if (handleKey.length > 0) emittedTypedAttdefHandles.add(handleKey);
      }
      // FINDING 1 — propagate the conformant `forceLayerEmit` so the typed ATTDEF
      // emits group 8 (layer) like the other block children built via `toDxfEntity`
      // below, instead of silently omitting code 8 for layer "0".
      childDxfEntities.push(
        drawingAttdefToDxfAttdef(attdef, blockSpace, { forceLayerEmit }),
      );
      continue;
    }
    // BlockChildEntity is structurally a subset of DrawingEntity for the
    // remaining types (Line / Point / Circle / Arc / Ellipse / Spline /
    // LWPOLYLINE / Text / MText / Block / Hatch / Dimension / Multileader).
    // `toDxfEntity` is typed against DrawingEntity so we narrow via
    // `as DrawingEntity` — the discriminated-union `type` field is identical.
    childDxfEntities.push(
      toDxfEntity(child as unknown as DrawingEntity, blockSpace, {
        forceLayerEmit,
        blocks,
      }),
    );
  }
  // FROM-SCRATCH — suppress the RAW ATTDEF when an emitted TYPED ATTDEF already
  // covers its identity (the typed one carries the reminted, ref-targeted handle).
  // IMPORTED — every raw ATTDEF emits verbatim (the matching typed projection was
  // suppressed above), preserving carry-forward byte-fidelity. A raw ATTDEF with no
  // typed counterpart emits on either path.
  appendRawEntityGroupsWithAttdefDedup(
    childDxfEntities,
    block.rawEntityGroups,
    docIsImported,
    emittedTypedAttdefTags,
    emittedTypedAttdefHandles,
    forceLayerEmit,
  );
  return childDxfEntities;
}

/**
 * Build a DxfBlocksSection from the drawing-model `document.blocks` map.
 * Each BlockDefinition becomes a DxfBlockDefinition:
 *
 *   - `block.entities` (typed BlockChildEntity[]) — each Line / Circle /
 *     Arc / LWPOLYLINE / Text / MText / Block is fed through `toDxfEntity`
 *     (which already knows how to serialise DrawingEntity variants).
 *     Attdef is deliberately SKIPPED from the typed branch because its
 *     verbatim raw counterpart lives in `block.rawEntityGroups` and the
 *     Attdef → DxfAttdefEntity encoder does not yet exist. Emitting only
 *     the raw groups avoids lossy double-encoding.
 *   - `block.rawEntityGroups` — every unsupported child (ACAD_TABLE,
 *     SPLINE, XRECORD, legacy POLYLINE, IMAGE, 3DFACE, …) is converted
 *     back to a DxfUnsupportedEntity so the writer re-emits the bytes
 *     unchanged.
 *
 * Returns `null` when the document has no blocks to emit, so the caller
 * can omit the BLOCKS section entirely and let the stub-builder in
 * `serializeDxfDocument` inject one only if DIMENSION entities require it.
 */
export function buildBlocksSectionFromDrawingBlocks(
  document: DrawingDocument,
  /**
   * round-16 BUG 2 — whether the EXPORTED document is imported-source, evaluated
   * on the ORIGINAL pre-`ensureConformantSkeleton` input (NOT `document`). The
   * conformant-skeleton back-fill INJECTS `rawPassthroughSections` /
   * `rawTableGroups` / `rawObjectGroups` onto a from-scratch document, so a
   * post-skeleton from-scratch `document` is INDISTINGUISHABLE from a true
   * imported-source one by `isImportedSourceDocument(document)`. The caller
   * therefore captures `isImportedSourceDocument(inputDocument)` BEFORE the
   * skeleton runs and threads it here so the ATTDEF de-dup picks the right branch
   * (imported-source ⇒ keep the byte-faithful raw ATTDEF; from-scratch ⇒ keep the
   * reminted typed ATTDEF). Optional with a `provenance`-only fallback for legacy
   * test/AI callers that build a partial document directly (their `document` IS
   * the source — no skeleton ran — so `provenance` alone is the right signal).
   */
  importedSource: boolean = document.provenance === "imported",
  /**
   * #314 redesign — the paper-space export placement plan (computed once by the
   * orchestrator and shared with the ENTITIES loop so the two sites stay
   * symmetric). EXTRA paper tabs are reconstructed into their `*Paper_Space<N>`
   * BLOCK definitions HERE from their PAIRED SPACE's entities (the space is the
   * edit-authoritative copy; the importer leaves the paper BLOCK definitions
   * empty). Optional with a recompute fallback for legacy direct callers (tests,
   * AI command builders) that invoke this function without the orchestrator.
   */
  paperPlan: PaperSpaceLayoutPlan = buildPaperSpaceLayoutPlan(document),
  /**
   * #314 round-2 F1 — the SAME effective per-space entity source the ENTITIES
   * loop iterates (`effectiveSnapshot` = the compact-cache `entitiesSnapshot`,
   * dropped to `undefined` when the conformant-skeleton reminted). In the
   * streaming / compact-cache path the inline `space.entities` arrays are EMPTY;
   * the relocated EXTRA-tab geometry lives ONLY in the snapshot. The paper-block
   * rebuild therefore reads `entitiesSnapshot?.get(space.id) ?? space.entities`
   * so the extra tabs' geometry is not dropped on a streamed save. Optional with
   * an `undefined` default for legacy direct callers (tests, AI builders) that
   * always carry inline entities.
   */
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
  /**
   * FIX — when true, force an explicit `8/<layer>` on every block-definition
   * child entity (user decision: "write code 8 on every entity"), matching the
   * conformant from-scratch ENTITIES-section path. The orchestrator passes
   * `reminted` (the conformant-skeleton discriminator). Defaults to `false` so
   * the carry-forward / imported round-trip stays byte-identical (code 8 omitted
   * for the default layer "0").
   */
  forceLayerEmit: boolean = false,
): DxfBlocksSection | null {
  // Legacy callers (test fixtures, AI command builders) sometimes construct
  // a DrawingDocument partial without a `blocks` field — default to empty
  // rather than throwing, since the downstream stub builder in the writer
  // handles missing blocks gracefully.
  const blockEntries = Object.entries(document.blocks ?? {});
  // F2' (round-2) — DO NOT early-return when there are RELOCATED paper spaces to
  // synthesise. A shell carrying LAYOUT + BLOCK_RECORD metadata (so the handle
  // chain resolves) but an EMPTY `document.blocks` would otherwise lose every
  // `*Paper_Space<N>` tab: those spaces are EXCLUDED from the ENTITIES loop
  // (paperPlan.excludedSpaceIds) yet, with no `blocks` to iterate, the main loop
  // never emits their block AND the early-return skipped the defensive post-loop
  // synthesis pass below. Only return early when there is genuinely NOTHING to
  // emit (no blocks AND no excluded paper spaces to reconstruct).
  if (blockEntries.length === 0 && paperPlan.excludedSpaceIds.size === 0) {
    return null;
  }

  // Build a synthetic DrawingSpace per block so we can reuse `toDxfEntity`'s
  // common-props plumbing (layer / handle / xdata). The space only carries
  // the `id` / `kind` fields the existing machinery inspects, but we also
  // fill the schema-required arrays so strict exactOptionalPropertyTypes
  // checks pass (refeditWorkingSet is a required-with-default field on
  // DrawingSpace).
  const blocks: DxfBlockDefinition[] = [];
  // round-16 BUG 2 — the imported-source discriminator for the ATTDEF de-dup
  // (provenance marker OR imported raw SECTION source — see the long rationale at
  // the regular-block branch). Hoisted to function scope so BOTH the regular
  // branch and the #314 EXTRA-tab paper-block branch (F2) share one value.
  const docIsImported = importedSource;
  // #314 redesign — track which EXTRA-tab paper blocks we successfully emitted
  // from their paired space, so the defensive post-loop pass can synthesise a
  // block shell for any extra tab whose `*Paper_Space<N>` block is missing from
  // `document.blocks` (content would otherwise be silently lost). F2'' (round-3)
  // — keys are stored LOWERCASED so the post-loop `.has()` is case-insensitive:
  // this set is added to with the BLOCK-DEFINITION name spelling but later
  // compared against the BLOCK_RECORD name spelling (the `paperSpaceByBlockName`
  // key), which may differ in casing for the same folded name.
  const emittedPaperBlockNames = new Set<string>();
  for (const [name, block] of blockEntries) {
    // #314 redesign — LAYOUT (anonymous space) blocks are NOT emitted from their
    // own `block.entities` / `block.rawEntityGroups`: the importer leaves those
    // EMPTY and the SPACE is the edit-authoritative copy.
    if (isLayoutBlockName(name)) {
      // F2'' (round-3) — CASE-INSENSITIVE pairing: the plan keys
      // `paperSpaceByBlockName` by the BLOCK_RECORD name spelling, but this loop
      // keys by the BLOCK-DEFINITION `name`. A record-vs-block case mismatch
      // (AutoCAD folds block names) would make a spelling-exact `.get(name)`
      // MISS → the paired tab is wrongly treated as unpaired (kept intact while
      // its space was already excluded from ENTITIES) → dropped/double-synthesized
      // geometry. `findPairedPaperSpaceEntry` folds both sides and returns the
      // matched CANONICAL key alongside the space.
      const pairedEntry = findPairedPaperSpaceEntry(
        paperPlan.paperSpaceByBlockName,
        name,
      );
      const pairedSpace = pairedEntry?.space;
      // round-4 P2 — EMIT the rebuilt paper block under the CANONICAL
      // BLOCK_RECORD-backed name (the matched `paperSpaceByBlockName` KEY), NOT
      // the `document.blocks` DEFINITION-key `name`. The importer routes paper
      // BLOCK content to its space CASE-SENSITIVELY (`dxfBridge.spaces.ts`
      // Pass 2, `draftByBlockName.get(block.name)` keyed by the BLOCK_RECORD
      // name); emitting a non-canonical spelling (e.g. `*PAPER_SPACE0` def vs
      // `*Paper_Space0` record) would make a save→re-import MISS the routing and
      // DROP this tab's block-owned geometry, and desync the LAYOUT/BLOCK_RECORD
      // linkage (those name the canonical record). An already-canonical block
      // name is returned unchanged, so the normal case is untouched.
      const emittedPaperName = pairedEntry?.canonicalName ?? name;
      if (isPaperSpaceBlockName(name) && pairedSpace) {
        // EXTRA paper tab — reconstruct its TYPED children from the PAIRED SPACE's
        // entities (pairedSpace.kind === "layout" ⇒ group 67 + 410 preserved
        // inside the block). The space is the edit-authoritative copy of the typed
        // geometry; we do NOT walk this block's own TYPED `block.entities` (the
        // importer's duplicate copy is empty).
        //
        // F1 (round-2) — read from the SAME effective per-space source the ENTITIES
        // loop uses (`entitiesSnapshot?.get(space.id) ?? space.entities`). In the
        // streaming / compact-cache path the inline `pairedSpace.entities` array is
        // EMPTY and the geometry lives ONLY in the snapshot; reading
        // `pairedSpace.entities` alone would DROP the relocated tab's geometry.
        //
        // F2 (round-2) — the importer's BYTE-FAITHFUL unsupported children
        // (ACAD_TABLE / IMAGE / …) are captured in `block.rawEntityGroups`, NOT
        // copied into `space.entities` (buildImportedSpaces only routes typed
        // children). Append them here — with the SAME provenance-aware ATTDEF
        // de-dup the regular-block branch uses — so they survive the relocation.
        // F2'' (round-3) — record the FOLDED name so the post-loop synthesis
        // pass (which iterates the record-name-keyed `paperSpaceByBlockName`)
        // recognises this tab as already-emitted regardless of the
        // record-vs-block name casing, preventing a second case-variant block.
        // round-4 — fold the CANONICAL key (same lowercase as `name`, but the
        // record-name is the source of truth the post-loop compares against).
        emittedPaperBlockNames.add(emittedPaperName.toLowerCase());
        const pairedEntities =
          entitiesSnapshot?.get(pairedSpace.id) ?? pairedSpace.entities;
        const paperChildren: DxfEntity[] = pairedEntities.map((e) =>
          // round-4 FINDING 1 — thread the blocks map (see buildBlockOwnChildren).
          // EXPORT-VP-FROZEN — thread the LAYER table so an extra-tab layout's
          // VIEWPORT resolves `frozenLayers` NAMES → native group-331 handles.
          toDxfEntity(e, pairedSpace, {
            forceLayerEmit,
            blocks: document.blocks ?? {},
            layers: document.styleTables?.layers ?? [],
          }),
        );
        // A paper SPACE's `entities` are DrawingEntity[] — the union has NO Attdef
        // variant (ATTDEFs live only in BlockDefinition.entities), so no typed
        // ATTDEF can be emitted from the paired space. The de-dup identity sets are
        // therefore empty; we still route the raw groups through the shared helper
        // (mirroring the regular branch exactly) so non-ATTDEF raw children emit
        // verbatim and any raw ATTDEF emits once.
        appendRawEntityGroupsWithAttdefDedup(
          paperChildren,
          block.rawEntityGroups,
          docIsImported,
          EMPTY_ATTDEF_IDENTITY_SET,
          EMPTY_ATTDEF_IDENTITY_SET,
          forceLayerEmit,
        );
        // round-4 P2 — emit under the CANONICAL record-backed name so the
        // case-sensitive importer routes this tab's block content back to its
        // space and the LAYOUT/BLOCK_RECORD linkage stays in sync.
        blocks.push(
          buildLayoutBlockDefinition(emittedPaperName, block, paperChildren),
        );
        continue;
      }
      // F3' (round-2) — a SUFFIXED `*Paper_Space<N>` block that is UNPAIRED
      // (`pairedSpace` undefined ⇒ the LAYOUT↔BLOCK_RECORD handle chain was
      // missing/broken so buildImportedSpaces could NOT route its content to a
      // space). Its content exists ONLY on the block def. It is NOT in
      // `excludedSpaceIds` (the handle-chain-only plan excludes only RESOLVED
      // spaces — no double-emit risk), so KEEP its children intact: emit the
      // block's OWN typed `block.entities` + its raw groups (with the same
      // provenance-aware ATTDEF de-dup the regular-block branch uses). Do NOT
      // empty it — that would silently DROP the tab's geometry.
      //
      // The un-suffixed `*Paper_Space` (case-insensitive — F4'/`isFirstPaperSpace
      // BlockName`) is EXCLUDED here: it is the first/active paper whose SUPPORTED
      // content is canonical in ENTITIES, so it takes the "empty supported, keep
      // raw" path below (emptying it would be correct; KEEPING its children would
      // DOUBLE-emit the first paper). Only a SUFFIXED unpaired tab keeps everything.
      // T9b review round 2 — the SHARED predicate (`blockEmitsOwnTypedChildren`,
      // the paired case was handled above) so the pre-scan passes follow the
      // same plan as this branch.
      if (blockEmitsOwnTypedChildren(name, paperPlan.paperSpaceByBlockName)) {
        const unpairedChildren = buildBlockOwnChildren(
          name,
          block,
          docIsImported,
          forceLayerEmit,
          document.blocks ?? {},
        );
        blocks.push(
          buildLayoutBlockDefinition(name, block, unpairedChildren),
        );
        continue;
      }
      // FIRST/active paper block (`*Paper_Space`, un-suffixed) OR `*Model_Space`
      // — their SUPPORTED content is canonical in the ENTITIES section, so emit
      // EMPTY SUPPORTED entities. But F1' (round-2): a `*Model_Space` /
      // first-`*Paper_Space` block can still carry UNSUPPORTED children
      // (ACAD_TABLE / IMAGE / …) in `block.rawEntityGroups` that the importer
      // stored on the BLOCK def (NOT routed to the space, which holds only typed
      // children). Those raw passthrough children must SURVIVE — only the
      // supported entities are emptied (they live in ENTITIES). Route the raw
      // groups through the shared helper (the paired space carries no typed
      // ATTDEF, so the identity sets are empty — non-ATTDEF raw emits verbatim,
      // a raw ATTDEF emits once).
      const rawOnlyChildren: DxfEntity[] = [];
      appendRawEntityGroupsWithAttdefDedup(
        rawOnlyChildren,
        block.rawEntityGroups,
        docIsImported,
        EMPTY_ATTDEF_IDENTITY_SET,
        EMPTY_ATTDEF_IDENTITY_SET,
        forceLayerEmit,
      );
      blocks.push(buildLayoutBlockDefinition(name, block, rawOnlyChildren));
      continue;
    }
    // Regular user block — emit its OWN typed children + raw groups (with
    // provenance-aware ATTDEF de-dup). Shared with the F3' UNPAIRED suffixed
    // paper-block branch via `buildBlockOwnChildren` (single source of truth for
    // the per-block ATTDEF de-dup rule).
    const childDxfEntities = buildBlockOwnChildren(
      name,
      block,
      docIsImported,
      forceLayerEmit,
      document.blocks ?? {},
    );
    blocks.push(buildBlockDefinitionShape(name, block, childDxfEntities));
  }

  // #314 redesign — DEFENSIVE: an EXTRA-tab paper space resolved (handle-chain) to
  // a RECORD-BACKED `*Paper_Space<N>` name whose BLOCK definition is MISSING from
  // `document.blocks` (the BLOCK_RECORD exists but the BLOCK entry was not created,
  // e.g. a layout added at runtime that minted a record but no block) would
  // otherwise lose its content entirely (it is already EXCLUDED from the ENTITIES
  // loop). Synthesise a minimal paper-block shell from the paired space so the
  // content survives. With the handle-chain-only plan every key here is a real
  // record-backed name (no synthetic collision). F1 — read the same effective
  // snapshot source so a streamed/compact-cache paired space is not dropped; the
  // paired space has NO typed ATTDEFs (DrawingEntity union) so no raw-group append
  // is needed here (a record-backed name with a `block` entry took the main branch
  // above, which DOES append raw groups).
  //
  // FINDING 3 (T9 round-2) — this defensive synthesis is the ONLY place a
  // record-backed extra tab whose BLOCK def is MISSING gets its paired-space
  // entities emitted, so it MUST also thread `forceLayerEmit` (the reminted flag):
  // without it those entities silently omit code 8, violating the "code 8 on every
  // entity" invariant the main branches enforce. Off the conformant path
  // (`forceLayerEmit` false) the behaviour is unchanged (code 8 omitted for "0").
  for (const [blockName, pairedSpace] of paperPlan.paperSpaceByBlockName) {
    // F2'' (round-3) — `emittedPaperBlockNames` is keyed by the FOLDED
    // BLOCK-DEFINITION name; fold this BLOCK_RECORD name to match so a
    // record-vs-block casing difference cannot re-synthesize an already-emitted
    // tab (which would double-emit a case-variant block).
    if (emittedPaperBlockNames.has(blockName.toLowerCase())) continue;
    const pairedEntities =
      entitiesSnapshot?.get(pairedSpace.id) ?? pairedSpace.entities;
    blocks.push(
      buildLayoutBlockDefinition(
        blockName,
        document.blocks?.[blockName],
        pairedEntities.map((e) =>
          // round-4 FINDING 1 — thread the blocks map (see buildBlockOwnChildren).
          // EXPORT-VP-FROZEN — thread the LAYER table so an extra-tab layout's
          // VIEWPORT resolves `frozenLayers` NAMES → native group-331 handles.
          toDxfEntity(e, pairedSpace, {
            forceLayerEmit,
            blocks: document.blocks ?? {},
            layers: document.styleTables?.layers ?? [],
          }),
        ),
      ),
    );
  }

  return { kind: "blocks-section", blocks, rawBlockGroups: [] };
}

/**
 * Assemble a {@link DxfBlockDefinition} from a model {@link BlockDefinition}
 * plus a pre-built child entity list. Shared by the regular user-block path and
 * the layout (paper / model space) block path so the header / base-point /
 * owner-handle plumbing has a single source of truth.
 */
function buildBlockDefinitionShape(
  name: string,
  block: BlockDefinition,
  entities: DxfEntity[],
): DxfBlockDefinition {
  return {
    name,
    handle: block.handle,
    // ownerHandle is optional on DxfBlockDefinition — include it only
    // when present to satisfy `exactOptionalPropertyTypes: true`.
    ...(block.ownerHandle !== undefined ? { ownerHandle: block.ownerHandle } : {}),
    layer: block.layer,
    flags: block.flags,
    basePoint: {
      x: block.basePoint.x,
      y: block.basePoint.y,
      z: block.basePoint.z ?? 0,
    },
    xrefPathName: block.xrefPathName,
    description: block.description,
    entities,
    // We do not (yet) round-trip the original ENDBLK subclass groups
    // verbatim — the writer reconstructs a minimal ENDBLK when this is
    // empty. Byte-level ENDBLK fidelity is a follow-up if needed.
    endblkGroups: [],
    // T9b — the ENDBLK's own group-5 (imported verbatim / skeleton-minted) so
    // the reconstructed ENDBLK carries `5 <handle>` + `330 <block owner>`.
    ...(block.endblkHandle !== undefined
      ? { endblkHandle: block.endblkHandle }
      : {}),
  };
}

/**
 * #314 redesign — build a layout (`*Model_Space` / `*Paper_Space` /
 * `*Paper_Space<N>`) BLOCK definition. The first/active `*Paper_Space` and
 * `*Model_Space` blocks pass `entities: []` (their content is canonical in the
 * ENTITIES section); an EXTRA `*Paper_Space<N>` tab passes its paired space's
 * converted entities. When the source `BlockDefinition` is missing (defensive
 * synthesis), a minimal `0`-base, layer-`0` shell is used.
 */
function buildLayoutBlockDefinition(
  name: string,
  block: BlockDefinition | undefined,
  entities: DxfEntity[],
): DxfBlockDefinition {
  if (block) return buildBlockDefinitionShape(name, block, entities);
  return {
    name,
    handle: "",
    layer: "0",
    flags: 0,
    basePoint: { x: 0, y: 0, z: 0 },
    xrefPathName: "",
    description: "",
    entities,
    endblkGroups: [],
  };
}
